import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, takeUntil } from 'rxjs';
import { ServiceSelectionEnum } from '../../../../../../core/enums/applications/service-selection-enum';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { RegExPattern } from '../../../../../../core/models/regexp-pattern/regex-pattern';
import { RegexInputDirective } from '../../../../../../shared/directives/regex-input/regex-input.directive';
import { ApplicationService } from '../../../../shared/services/application/application.service';
import { StepNavigationService } from '../../../../shared/services/step-navigation/step-navigation.service';
import { ApplicationPageFacadeService } from '../../../shared/services/application-page-facade/application-page-facade.service';
import { DirectEntryService } from '../../../shared/services/direct-entry/direct-entry.service';
import { SubmitToEwaFormSubmissionComponent } from '../shared/components/submit-to-ewa-form-submission/submit-to-ewa-form-submission.component';
import { SubmitToEwaPageBaseComponent } from '../shared/components/submit-to-ewa-page-base/submit-to-ewa-page-base.component';
import { EwaSubmissionResponse } from '../shared/models/ewa-submission-response';
import { SubmitToEwaDetailsResponse } from '../shared/models/submit-to-ewa-details-response';
import { SubmitToEwaFormService } from '../shared/services/submit-to-ewa-form.service';
import { MinimumEwaHandoverSubmitToEwaFormModel } from './models/minimum-ewa-handover-submit-to-ewa-form-model';
import { MinimumEwaHandoverSubmitToEwaRequest } from './models/minimum-ewa-handover-submit-to-ewa-request';
import { MinimumEwaHandoverSubmitToEwaValidatorsMap } from './types/minimum-ewa-handover-submit-to-ewa-validators-map';
import { padFrnWithLeadingZeros } from '../../../../../../core/functions/string-helpers';

@Component({
    selector: 'app-minimum-ewa-handover-submit-to-ewa',
    templateUrl: './minimum-ewa-handover-submit-to-ewa.component.html',
    imports: [
        MatSelectModule,
        MatFormFieldModule,
        FormsModule,
        ReactiveFormsModule,
        MatIconModule,
        MatInputModule,
        MatButtonModule,
        RegexInputDirective,
        SubmitToEwaFormSubmissionComponent,
    ],
})
export class MinimumEwaHandoverSubmitToEwaComponent
    extends SubmitToEwaPageBaseComponent<
        MinimumEwaHandoverSubmitToEwaRequest,
        MinimumEwaHandoverSubmitToEwaFormModel
    >
    implements OnInit, OnDestroy
{
    protected override readonly route: ActivatedRoute;
    protected override readonly router: Router;
    protected override readonly applicationPageFacadeService: ApplicationPageFacadeService;
    private readonly submitToEwaService = inject(SubmitToEwaFormService);
    private readonly applicationService = inject(ApplicationService);
    private readonly stepNavigationService = inject(StepNavigationService);
    private readonly directEntryService = inject(DirectEntryService);

    /**
     * The fields that the form requires one of to be populated to enable submission
     */
    readonly requiredFormFields: Array<
        keyof MinimumEwaHandoverSubmitToEwaFormModel
    > = [
        'licenseeEntityName',
        'licenseeFirstName',
        'licenseeLastName',
        'fccRegistrationNumber',
        'taxIdNumber',
    ];

    /**
     * The fields that the form requires all to be populated or none to be populated to enable submission.
     */
    readonly mutuallyConstrainedFields: Array<
        keyof MinimumEwaHandoverSubmitToEwaFormModel
    > = ['licenseeFirstName', 'licenseeLastName'];

    /**
     * The additional validators to add to the form controls.
     */
    readonly additionalControlValidators: MinimumEwaHandoverSubmitToEwaValidatorsMap =
        {
            licenseeEntityName: [],
            licenseeFirstName: [],
            licenseeLastName: [],
            fccRegistrationNumber: [],
            taxIdNumber: [Validators.minLength(9), Validators.maxLength(9)],
            notes: [],
        };

    /**
     * The default service selection for this form.
     */
    readonly serviceSelection: ServiceSelectionEnum =
        ServiceSelectionEnum.EXPRESS;

    regExPattern = RegExPattern;

    constructor() {
        const route = inject(ActivatedRoute);
        const router = inject(Router);
        const applicationPageFacadeService = inject(
            ApplicationPageFacadeService,
        );

        super(route, router, applicationPageFacadeService);

        this.route = route;
        this.router = router;
        this.applicationPageFacadeService = applicationPageFacadeService;
    }

    override form: FormGroup<
        FormGroupType<MinimumEwaHandoverSubmitToEwaFormModel>
    > = new FormGroup({
        licenseeEntityName: new FormControl<string | null>(null),
        licenseeFirstName: new FormControl<string | null>(null),
        licenseeLastName: new FormControl<string | null>(null),
        fccRegistrationNumber: new FormControl<string | null>(null),
        taxIdNumber: new FormControl<string | null>(null),
        notes: new FormControl<string | null>(null),
    });

    override ngOnInit(): void {
        super.ngOnInit();
        this.setupFormListeners();
    }

    override ngOnDestroy(): void {
        /**
         * This final step may contain fields that are also present in previous steps.
         * To prevent validation issues when navigating back to those steps, we need to refresh the step validations for all steps.
         */
        this.directEntryService
            .getAndSetStepValidations(this.applicationId!, false)
            .subscribe();

        super.ngOnDestroy();
    }

    /**
     * Determines if the user is allowed to do a final submission.
     * @returns whether the user is allowed to do a final submission
     */
    enableSubmission(): boolean {
        return this.form.valid && !this.applicationState?.handoverToEwa;
    }

    /**
     * Submits the form to the EWA. Will do a modal popup to confirm the submission.
     * Then, will submit the form and redirect you to the dashboard.
     */
    handoverToEwaSubmission(): void {
        if (this.enableSubmission()) {
            this.submitToEwaService.handoverToEwaSubmission(
                () => this.saveFormDataToApi(this.generateRequestObject(true)),
                () => {
                    /**
                     * This is needed to prevent the guard from blocking the navigation
                     * since the submission has already been made and the user is being redirected.
                     */
                    this.hasChanges = false;
                },
            );
        } else {
            this.form.markAllAsTouched();
        }
    }

    padFrnString(value: string | null): void {
        const paddedValue = padFrnWithLeadingZeros(value);
        this.form.controls.fccRegistrationNumber.setValue(paddedValue);
    }

protected override bindForm(response: SubmitToEwaDetailsResponse): void {
    this.form.patchValue({
        ...response,
        fccRegistrationNumber: padFrnWithLeadingZeros(response.fccRegistrationNumber),
    });
}

    protected override getFormDataFromApi(
        applicationId: string,
    ): Observable<SubmitToEwaDetailsResponse> {
        return this.applicationService.getDataForFinalSubmission(applicationId);
    }

    protected override saveFormDataToApi(
        request: MinimumEwaHandoverSubmitToEwaRequest,
    ): Observable<EwaSubmissionResponse> {
        return this.applicationService.finalSubmissionToEwa(request);
    }

    protected override generateRequestObject(
        isFinalSubmission: boolean = false,
    ): MinimumEwaHandoverSubmitToEwaRequest {
        const formValue = this.form
            .value as Required<MinimumEwaHandoverSubmitToEwaRequest>;

        return {
            ...formValue,
            submitToEwa: isFinalSubmission,
            applicationId: this.applicationId!,
        };
    }

    /**
     * Sets up the form listeners to update the form validators based on the populated fields
     */
    private setupFormListeners(): void {
        this.form.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.updateFormValidators());
    }

    /**
     * Updates the form validators based on the populated fields.
     */
    private updateFormValidators(): void {
        this.toggleValidatorsToControls(
            this.requiredFormFields,
            this.hasPopulatedRequiredControls(),
            (removeRequiredValidator, control) =>
                removeRequiredValidator && !control.value,
        );
        this.toggleValidatorsToControls(
            this.mutuallyConstrainedFields,
            this.shouldRemoveRelatedFieldValidators(),
            (removeRequiredValidator) => removeRequiredValidator,
        );
    }

    /**
     * Toggles the required validators on the form controls based on the condition.
     * If the condition is met, the required validator is removed, otherwise it is added.
     *
     * The additional validators are always added to the control.
     *
     * @param formFields - The form controls to update
     * @param removeValidator - Whether to remove the validator
     * @param condition - A function determining when to remove/add validators
     */
    private toggleValidatorsToControls(
        formFields: Array<keyof MinimumEwaHandoverSubmitToEwaFormModel>,
        removeRequiredValidator: boolean,
        condition: (
            removeRequiredValidator: boolean,
            control: FormControl,
        ) => boolean,
    ): void {
        formFields.forEach((controlName) => {
            const control = this.form.controls[controlName] as FormControl;

            if (condition(removeRequiredValidator, control)) {
                control.removeValidators(Validators.required);
            } else {
                control.addValidators(Validators.required);
            }

            control.addValidators(
                this.additionalControlValidators[controlName],
            );

            control.markAsTouched();
            control.updateValueAndValidity({ emitEvent: false });
        });
    }

    /**
     * Checks if any of the required fields are populated.
     * @returns Whether any of the required fields are populated
     */
    private hasPopulatedRequiredControls(): boolean {
        return this.requiredFormFields.some(
            (field) => !!this.form.controls[field].value,
        );
    }

    /**
     * Checks if all of the related fields are empty.
     * @returns Whether all of the related fields are empty
     */
    private areAllMutuallyConstrainedFieldsEmpty(): boolean {
        return this.mutuallyConstrainedFields.every(
            (field) => !this.form.controls[field].value,
        );
    }

    /**
     * Checks to see if the related fields should have their required validators removed if all of the required fields are empty or populated.
     * @returns Whether the related fields should have their required validators removed
     */
    private shouldRemoveRelatedFieldValidators(): boolean {
        return (
            this.hasPopulatedRequiredControls() &&
            this.areAllMutuallyConstrainedFieldsEmpty()
        );
    }
}
